import React, { useState, useEffect, useRef, useContext } from "react";
import { useTheme } from "@emotion/react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Button, TextField, Divider, Grid, Switch, Tooltip } from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useAllCompany } from "../../hook/useAllCompany.js";
import { useUserRole } from "../../hook/useUserRole.js";
import { useAllUsers } from "../../hook/useAllUsers.js";
import { useUser } from "../../hook/useUser.js";
import { useProjectStatus } from "../../hook/useProjectStatus.js";
import { getElementStatus } from "../../utils/schedule.js";
import { getProjectDetailsDataApi, updateProjectApi } from "../../api/project";
import {
  getMembersApi,
  getContractorsApi,
  removeMembersApi,
  addOrUpdateMembersApi,
  addContractorApi,
  updateContractorApi,
  removeContractorApi,
  updatePermissionApi,
} from "../../api/member";
import { getElementsDataApi, getMainModelDataApi, getBackgroundModelUrlApi } from "../../api/model";
// import { getContractTypeApi } from "../../api/predefine.js";
import { getAllUserApi } from "../../api/user.js";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import IFCViewer from "../../component/bim/IFCViewer.js";
import MainTable from "../../component/table/MainTable.js";
import SelectComponent from "../../component/SelectComponent.js";
import Popup from "../../component/popup/Popup.js";
import ConfirmPopup from "../../component/popup/ConfirmPopup.js";
import AddNewMemberPopup from "./components/AddNewMemberPopup.js";
import AddNewContractorPopup from "./components/AddNewContractorPopup.js";
import FullScreenLoading from "../../component/FullScreenLoading.js";

const switchStyle = {
  width: 80,
  height: 40,
  "& .MuiSwitch-thumb": {
    width: "16px",
    height: "16px",
    mt: "3px",
    ml: "3px",
  },
  "& .Mui-checked": {
    transform: "translateX(42px)!important",
    color: "#fff!important",
  },
  "& .Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#00A3E0 !important",
    opacity: "1 !important",
  },
};

const tooltipStyle = {
  minWidth: "600px",
  backgroundColor: "#E6E6E6",
  color: "#000",
  fontSize: "12px",
  p: 1,
  borderRadius: "4px",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  whiteSpace: "pre-line",
};

const datePickerStyle = {
  flex: 1,
  ml: 1,
  borderRadius: "4px",
  background: "#dbe1eb",
  "& .MuiInputBase-input": {
    pl: 2,
    pr: 0,
    py: 0.5,
  },
  "& .MuiIconButton-root.Mui-disabled": {
    color: "transparent!important",
  },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: "#000",
  },
  "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent!important",
  },
};

const typographyStyle = {
  color: "#333",
  py: 1,
  fontWeight: 700,
  fontSize: "12px",
  minWidth: 120,
};

const tableStyle = {
  height: "calc(100% - 52px)",
  maxHeight: "calc(100% - 52px)",
  ".MuiButtonBase-root.MuiTableSortLabel-root.Mui-active": {
    color: "#fff",
  },
  ".MuiButtonBase-root.MuiTableSortLabel-root:hover": {
    color: "#fff",
  },
  ".MuiTableSortLabel-icon": {
    color: "#fff!important",
    opacity: "1!important",
  },
};

const textFieldStyle = {
  my: 1,
  ml: 1,
  flex: 1,
  borderRadius: "4px",
  background: "#dbe1eb",
  "& .MuiInputBase-input": {
    py: 0.5,
    px: 0,
  },
  "& .MuiInputBase-root": {
    px: 2,
    py: 0,
  },
};

const buttonStyle = {
  lineHeight: "normal",
  my: 2,
  borderRadius: "10px",
};

const ProjectDetails = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const container = useRef();
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const { user } = useUser(projectId);
  const allCompany = useAllCompany();
  const userRoleOptions = useUserRole();
  const allUsers = useAllUsers();
  const projectStatus = useProjectStatus();
  const renderTextField = (label, field, placeholder, multiline = false, rows = 1, disabled = false) => (
    <Grid item xs={multiline ? 12 : 6} sx={{ display: "flex", alignItems: multiline ? "start" : "center" }}>
      <Typography sx={typographyStyle}>{label}</Typography>
      {disabled ? (
        <Typography sx={{ ...typographyStyle, fontWeight: 500, ml: 1.5 }}>{state[field]}</Typography>
      ) : (
        <TextField
          value={state[field]}
          onChange={(e) => updateState(field, e.target.value)}
          placeholder={placeholder}
          variant="outlined"
          sx={textFieldStyle}
          multiline={multiline}
          rows={rows}
          inputProps={{ maxLength: 200 }}
        />
      )}
    </Grid>
  );

  const renderDatePicker = (label, field, disabled = false) => (
    <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
      <Typography component="span" sx={typographyStyle}>
        {label}
      </Typography>
      {disabled ? (
        <Typography component="span" sx={{ ...typographyStyle, fontWeight: 500, ml: 1.5 }}>
          {dayjs(state[field]).format("DD/MM/YYYY")}
        </Typography>
      ) : (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label=""
            value={dayjs(state[field])}
            onChange={(date) => updateState(field, date)}
            format="DD/MM/YYYY"
            inputFormat="DD/MM/YYYY"
            renderInput={(params) => <TextField {...params} />}
            sx={datePickerStyle}
          />
        </LocalizationProvider>
      )}
    </Grid>
  );
  const Assai_note = `Please note:\n\n The “Client ID” can be found in the Assai Developer Portal or API Management section  after your register an account in Assai, which is a unique identifier for the application.\n
The “Secret Token” can be found in the Assai Developer Portal or API Management section after you register an account in Assai, which acts as a password for the application.Both values are essential for authenticating API requests.`;
  const ACC_note = `Please note:\n\n The “client ID” is essentially your application’s username and can be found in the My Apps section of the Autodesk DevPortal. It is a 32-character alphanumeric string (e.g., 5zw90va0UuwMKTnPS5sLsdgZjDkVYXN7). Your application passes it as the value for the client_id query parameter, and APS returns it in the client_id attribute in the returned JSON payload. \n
The “client secret” is essentially your application’s password and can be found next to the client ID in the My Apps section of the Autodesk DevPortal. It is a 16-character alphanumeric string (e.g., 7I6uN1rjneirxiMW), and your application passes it as the value for the client_secret query parameter.\n
The “revit urn (Uniform Resource Name)” is typically formatted as a string that includes the project ID and the specific model ID. For example, a Revit URN might look like urn:adsk.objects:os.object:myproject/myrevitmodel. It normally can be found in the Autodesk Forge viewer, Autodesk BIM 360, or API Responses.`;

  const membersTableHead = [
    { id: "memberName", label: "Name", align: "left" },
    { id: "company", label: "Company", align: "left" },
    { id: "accessLevel", label: "Access Level", align: "left" },
    { id: "editRight", label: "Edit Right", align: "center", type: "switch" },
    {
      id: "reportModule",
      label: "Report Module",
      align: "center",
      type: "switch",
    },
    { id: "costAccess", label: "Cost Module", align: "center", type: "switch" },
    { id: "status", label: "Account Status", align: "left" },
    {
      id: "lastLogin",
      label: "Last Login",
      align: "left",
      type: "dateWithTime",
    },
  ];

  const contractorsTableHead = [
    { id: "company", label: "Company", align: "left" },
    // { id: "category", label: "Category", align: "left" },
    { id: "pmName", label: "Project PM Name", align: "left" },
    { id: "pmEmail", label: "Project PM Email", align: "left" },
    { id: "costModule", label: "Cost Module", align: "center", type: "switch" },
  ];

  const [elementIds, setElementIds] = useState({
    planProgress: [],
    actualProgress: [],
    delayed: [],
    planComplete: [],
    actualComplete: [],
    showFilter: [],
    white: [],
    all: [],
  });
  const [commonProps, setCommonProps] = useState({});
  const [state, setState] = useState({
    originalState: {},
    membersTableData: [],
    originalMembersTableData: [],
    contractorsTableData: [],
    originalContractorsTableData: [],
    searchMemberValue: "",
    searchContractorValue: "",
    showCreateProject: false,
    // categoryOptions: [],
    allUserOptions: [],
    userByCompanyOptions: [],
    mainContactNameOptions: [],
    mainContactPersonName: "",
    mainContactPersonEmail: "",
    editProjectDetails: false,
    activeTab: "details",
    assaiEnabled: false,
    accEnabled: false,
    accClientId: "",
    accClientSecret: "",
    accRevitUrn: "",
    assaiClientId: "",
    assaiSecretToken: "",
    mainContactPersonUserId: "",
    modelURL: [],
    backgroundModels: [],
    elementsData: [],
    currentDate: dayjs(),
    statusOptions: [],
    statusValue: "",
    pendingSwitchUpdate: {},
    isLoadingMember: false,
    isLoadingContractor: false,
    isLoadingFullScreen: false,
    memberPage: 0,
    memberRowsPerPage: 10,
    contractorPage: 0,
    contractorRowsPerPage: 10,
  });

  const [updateMemberPopup, setUpdateMemberPopup] = useState(false);
  const [projectAccessInfor, setProjectAccessInfor] = useState({});
  const [removeMemberPopup, setRemoveMemberPopup] = useState(false);
  const [removeMemberMessage, setRemoveMerberMessage] = useState(null);
  const [updateContractorPopup, setUpdateContractorPopup] = useState(false);
  const [contractorInfor, setContractorInfor] = useState({});
  const [removeContractorPopup, setRemoveContractorPopup] = useState(false);
  const [addNewMemberPopup, setAddNewMemberPopup] = useState(false);
  const [addNewContractorPopup, setAddNewContractorPopup] = useState(false);
  const [updatePermissionPopup, setUpdatePermissionPopup] = useState(false);
  const [updatePermissionMessage, setUpdatePermissionMessage] = useState(null);
  const [updateContractorCostPopup, setUpdateContractorCostPopup] = useState(false);
  const [updateContractorCostMessage, setUpdateContractorCostMessage] = useState(null);

  const membersIconColumns =
    user?.userRole === "System Administrator" || user?.accessLevel === 2
      ? [
          {
            label: "",
            width: 25,
            note: "Update member",
            image: "assets/icons/circlemore.svg",
            action: async (row, event) => {
              if (event) {
                event.stopPropagation();
              }
              if (row) {
                setUpdateMemberPopup(true);
                setProjectAccessInfor({
                  ...row,
                  accessLevel: row.accessLevel,
                  editRight: row.editRight,
                  reportModule: row.reportModule,
                  costAccess: row.costAccess,
                });
              }
            },
          },
          {
            label: "",
            width: 25,
            note: "Remove member",
            image: "assets/icons/close.svg",
            action: async (row, event) => {
              if (event) {
                event.stopPropagation();
              }
              if (row) {
                setRemoveMemberPopup(true);
                setRemoveMerberMessage(`Are you sure to remove ${row.memberName} from the project?`);
                setProjectAccessInfor(row);
              }
            },
          },
        ]
      : [];

  const contractorsIconColumns =
    user?.userRole === "System Administrator" || user?.accessLevel === 2
      ? [
          {
            label: "",
            width: 25,
            note: "Update contractor",
            image: "assets/icons/circlemore.svg",
            action: async (row, event) => {
              if (event) {
                event.stopPropagation();
              }
              if (row) {
                try {
                  const currentCompanyId = allCompany?.find((option) => option.label === row.company)?.value;
                  const result = await getAllUserApi(currentCompanyId);
                  if (result?.data) {
                    // const selectedCategory = state.categoryOptions.find((option) => option.label === row.contractType);
                    const selectedPmName = state.allUserOptions.find((option) => option.label === row.pmName);
                    const filteredData = result.data.filter((item) => item.status !== "Disable");
                    setState((prevState) => ({
                      ...prevState,
                      userByCompanyOptions: filteredData?.map((item) => ({
                        ...item,
                        value: item.userId,
                        label: `${item.firstName} ${item.lastName}`,
                      })),
                    }));
                    setUpdateContractorPopup(true);
                    setContractorInfor({
                      ...row,
                      // category: selectedCategory?.value || "",
                      pmName: selectedPmName?.value || "",
                    });
                  }
                } catch {
                  showError("Update contractor occurred error.");
                }
              }
            },
          },
          {
            label: "",
            width: 25,
            note: "Remove contractor",
            image: "assets/icons/close.svg",
            action: async (row, event) => {
              if (event) {
                event.stopPropagation();
              }
              if (row) {
                setRemoveContractorPopup(true);
                setRemoveMerberMessage(`Are you sure to remove ${row.pmName} (${row.company}) from the project?`);
                setContractorInfor(row);
              }
            },
          },
        ]
      : [];

  const updateState = (field, value) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const isFormValid = () => {
    const { projectName, startDate, endDate, mainContactPersonUserId } = state;

    const baseFieldsFilled = projectName !== "" && startDate !== "" && endDate !== "" && mainContactPersonUserId !== "";

    if (!baseFieldsFilled) {
      return false;
    }

    const hasStartDate = dayjs(startDate).isValid();
    const hasEndDate = dayjs(endDate).isValid();

    if (hasStartDate && hasEndDate && dayjs(endDate).isBefore(dayjs(startDate))) {
      return false;
    }

    return true;
  };

  const getAllUserData = async () => {
    const formattedOptions = allUsers
      .map((item) => ({
        ...item,
        value: item.userId,
        label: `${item.firstName} ${item.lastName}`,
      }))
      .sort((a, b) => {
        const labelA = a.label?.trim().charAt(0).toUpperCase() || "";
        const labelB = b.label?.trim().charAt(0).toUpperCase() || "";

        if (!labelA) return 1;
        if (!labelB) return -1;

        return labelA.localeCompare(labelB);
      });
    const userEmailMap = allUsers.reduce((acc, item) => {
      acc[item.userId] = item.email;
      return acc;
    }, {});
    updateState("allUserOptions", formattedOptions);
    updateState("userEmailMap", userEmailMap);
  };

  const getProjectDetailsData = async () => {
    try {
      const result = await getProjectDetailsDataApi(projectId);

      if (result.data && result.data.length > 0) {
        setState((prevState) => ({
          ...prevState,
          ...result.data[0],
          originalState: result.data[0],
          mainContactPersonName:
            prevState.mainContactNameOptions.find((option) => option.value === result.data[0].mainContactPersonUserId)?.label || "",
          statusValue: prevState.statusOptions.find((option) => option.label === result.data[0].status)?.value || "",
        }));
      }
    } catch {
      showError("Received project detail data occurred error.");
    }
  };

  const getMembersTableData = async () => {
    try {
      updateState("isLoadingMember", true);
      const result = await getMembersApi(projectId);
      if (result?.data) {
        let filteredData = result.data;

        if (user?.userRole === "Contractor") {
          filteredData = result.data.filter((item) => item.company === user.company);
        }

        const membersTableData = filteredData.map((item) => ({
          ...item,
          memberName: `${item.firstName} ${item.lastName}`,
          userRole: userRoleOptions?.find((option) => option.value === item.userRole)?.label || item.userRole,
          id: item.userId,
        }));
        updateState("membersTableData", membersTableData);
        updateState("originalMembersTableData", membersTableData);
      }
    } catch {
      showError("Received project member data occurred error.");
    } finally {
      updateState("isLoadingMember", false);
    }
  };

  const getContractorsTableData = async () => {
    try {
      updateState("isLoadingContractor", true);
      const result = await getContractorsApi(projectId);
      if (result?.data) {
        const contractorsTableData = result.data.map((item) => ({
          ...item,
          company: item?.name,
          // category: item?.contractType,
          pmName: item?.pm?.firstName + " " + item?.pm?.lastName,
          pmEmail: item?.pm?.email,
          id: item.projectContractorId,
        }));
        updateState("contractorsTableData", contractorsTableData);
        updateState("originalContractorsTableData", contractorsTableData);
      }
    } catch {
      showError("Received project contractor data occurred error.");
    } finally {
      updateState("isLoadingContractor", false);
    }
  };

  const getCLPPersonData = async () => {
    const CLPId = allCompany.find((item) => item.label === "CLP").value;
    try {
      const result = await getAllUserApi(CLPId);
      if (result?.data) {
        const filteredData = result.data.filter((item) => item.status !== "Disable");
        const formattedMembers = filteredData?.map((item) => ({
          ...item,
          value: item.userId,
          label: `${item.firstName} ${item.lastName}`,
        }));

        updateState("mainContactNameOptions", formattedMembers);
      }
    } catch {
      showError("Received main contact person data occurred error.");
    }
  };

  // const getContractTypeData = async () => {
  //   try {
  //     const result = await getContractTypeApi();
  //     if (result?.data) {
  //       updateState("categoryOptions", result.data);
  //     }
  //   } catch {
  //     showError("Received contract type data occurred error.");
  //   }
  // };

  const contactNameChange = (value) => {
    const selectedEmail = state.userEmailMap[value] || "";
    updateState("mainContactPersonUserId", value);
    updateState("mainContactPersonEmail", selectedEmail);
  };

  //update project details
  const updateProjectDetails = async () => {
    const params = {
      projectName: state.projectName,
      projectNumber: state.projectNumber,
      startDate: dayjs(state.startDate).format("YYYY-MM-DD"),
      endDate: dayjs(state.endDate).format("YYYY-MM-DD"),
      description: state.description,
      location: state.location,
      mainContactPersonUserId: state.mainContactPersonUserId,
      assiaEnabled: state.assaiEnabled,
      assiaClientId: state.assaiClientId,
      assiaSecretToken: state.assaiSecretToken,
      accEnabled: state.accEnabled,
      accClientId: state.accClientId,
      accClientSecret: state.accClientSecret,
      accRevitURN: state.accRevitUrn,
      status: state.statusValue,
    };
    try {
      const result = await updateProjectApi(projectId, params);
      if (result?.data) {
        showSuccess("The project has been updated.");
        setState((prevState) => ({
          ...prevState,
          editProjectDetails: !prevState.editProjectDetails,
        }));
        getProjectDetailsData();
      }
    } catch {
      showError("Update project details failed.");
    }
  };

  //update member access
  const updateProjectAccessButton = async () => {
    const params = [
      {
        userId: projectAccessInfor.userId,
        accessLevel: projectAccessInfor.accessLevel === "Project Member" ? 1 : 2,
        editRight: projectAccessInfor.editRight,
        reportModule: projectAccessInfor.reportModule,
        costAccess: projectAccessInfor.costAccess,
        isPM: false,
      },
    ];
    try {
      const result = await addOrUpdateMembersApi(projectId, params);
      if (result?.data) {
        showSuccess("The project Access has been updated.");
        setUpdateMemberPopup(false);
        getMembersTableData();
      }
    } catch {
      showError("The project Access update failed.");
    }
  };

  const updateContractorButton = async () => {
    const params = [
      {
        companyId: allCompany?.find((option) => option.label === contractorInfor.company)?.value,
        // contractType: contractorInfor.category,
        userId: contractorInfor.pmName,
        costModule: contractorInfor.costModule,
      },
    ];
    try {
      const result = await updateContractorApi(projectId, params);
      if (result?.data) {
        showSuccess("The contractor has been updated.");
        setUpdateContractorPopup(false);
        getContractorsTableData();
      }
    } catch {
      showError("The contractor update failed.");
    }
  };
  //remove member from project
  const removeMembersButton = async () => {
    try {
      const result = await removeMembersApi(projectId, projectAccessInfor.projectUserId);
      if (result?.data) {
        showSuccess("User has been successfully removed from the project.");
        setRemoveMemberPopup(false);
        getMembersTableData();
      }
    } catch {
      showError("Remove member failed.");
    }
  };
  //remove contractor from project
  const removeContractorButton = async () => {
    try {
      const result = await removeContractorApi(projectId, contractorInfor.projectContractorId);
      if (result?.data) {
        showSuccess("User has been successfully removed from the project.");
        setRemoveContractorPopup(false);
        getContractorsTableData();
      }
    } catch {
      showError("Remove contractor failed.");
    }
  };
  //add new member
  const handleAddMember = async (values) => {
    try {
      const params = values.map((item) => ({
        userId: item.userId,
        accessLevel: item.accessLevel,
        editRight: item.editRight,
        reportModule: item.reportModule,
        costAccess: item.costAccess,
        isPM: false,
      }));
      const result = await addOrUpdateMembersApi(projectId, params);
      if (result?.data) {
        showSuccess("New users have been successfully added to the project.");
        setAddNewMemberPopup(false);
        getMembersTableData();
      }
    } catch {
      showError("Add new users failed.");
    }
  };

  const handleAddContractor = async (value) => {
    try {
      const params = value.map((item) => ({
        companyId: item.companyValue,
        // contractType: item.categoryValue,
        userId: item.pmNameValue,
        costModule: item.costModule,
      }));
      const result = await addContractorApi(projectId, params);
      if (result?.data) {
        setAddNewContractorPopup(false);
        showSuccess("New users have been successfully added to the project.");
        getContractorsTableData();
      }
    } catch {
      showError("Add new users failed.");
    }
  };

  //confirm update member permission
  const confirmUpdatePermission = async () => {
    const { rowId, columnName, switchValue, originalRow } = state.pendingSwitchUpdate || {};
    if (!rowId || !columnName || !originalRow || switchValue === undefined) {
      showError("The project Access update occured error.");
      setUpdatePermissionPopup(false);
      return;
    }
    try {
      const updatedRow = {
        ...originalRow,
        [columnName]: switchValue,
      };
      const result = await updatePermissionApi(
        projectId,
        updatedRow.projectUserId,
        updatedRow.accessLevel === "Project Member" ? 1 : 2,
        updatedRow.editRight,
        updatedRow.reportModule,
        updatedRow.costAccess
      );

      if (result?.data) {
        const updatedData = state.membersTableData.map((row) => (row.id === rowId ? { ...row, [columnName]: switchValue } : row));
        updateState("membersTableData", updatedData);
        updateState("originalMembersTableData", updatedData);
        updateState("pendingSwitchUpdate", null);
        showSuccess("The project Access has been updated.");
      }
    } catch {
      showError("The project Access update failed.");
    } finally {
      setUpdatePermissionPopup(false);
    }
  };

  //confirm update contractor cost module
  const confirmUpdateContractorCost = async () => {
    const { rowId, columnName, switchValue, originalRow } = state.pendingSwitchUpdate || {};
    if (!rowId || !columnName || !originalRow || switchValue === undefined) {
      showError("The contractor cost module Access update occured error.");
      setUpdateContractorCostPopup(false);
      return;
    }
    try {
      const updatedRow = {
        ...originalRow,
        [columnName]: switchValue,
      };
      const params = [
        {
          companyId: allCompany?.find((option) => option.label === updatedRow.company)?.value,
          // contractType: state.categoryOptions.find((option) => option.label === updatedRow.contractType)?.value || "",
          userId: allUsers?.find((option) => `${option.firstName} ${option.lastName}` === updatedRow.pmName)?.userId || "",
          costModule: switchValue,
        },
      ];
      const result = await updateContractorApi(projectId, params);

      if (result?.data) {
        const updatedData = state.contractorsTableData.map((row) => (row.id === rowId ? { ...row, [columnName]: switchValue } : row));
        updateState("contractorsTableData", updatedData);
        updateState("originalContractorsTableData", updatedData);
        updateState("pendingSwitchUpdate", null);
        showSuccess("The contractor cost module Access has been updated.");
        getMembersTableData();
      }
    } catch {
      showError("The contractor cost module Access update failed.");
    } finally {
      setUpdateContractorCostPopup(false);
    }
  };

  const searchTable = (originalDataKey, searchValueKey, targetDataKey, loading) => {
    updateState(loading, true);

    const { [originalDataKey]: originalTableData, [searchValueKey]: searchValue } = state;
    const normalizedSearchValue = String(searchValue || "")
      .trim()
      .toLowerCase();

    let filteredData = [...originalTableData];

    if (normalizedSearchValue) {
      filteredData = filteredData.filter((item) =>
        Object.values(item).some((value) => {
          if (typeof value === "string" || typeof value === "number") {
            const normalizedValue = String(value).toLowerCase();
            return normalizedValue.includes(normalizedSearchValue);
          }
          return false;
        })
      );
    }

    updateState(targetDataKey, filteredData);

    setTimeout(() => {
      updateState(loading, false);
    }, 100);
  };

  const searchMembersButton = () => {
    updateState("memberPage", 0);
    searchTable("originalMembersTableData", "searchMemberValue", "membersTableData", "isLoadingMember");
  };

  const searchContractorsButton = () => {
    updateState("contractorPage", 0);
    searchTable("originalContractorsTableData", "searchContractorValue", "contractorsTableData", "isLoadingContractor");
  };

  //get main model url and workset
  const getMainModel = async () => {
    const result = await getMainModelDataApi(projectId);
    const data = result?.data;

    if (data && Array.isArray(data)) {
      const mainModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: true,
        };
      });
      updateState("modelURL", mainModels);
      await getElementsData();
    }
  };

  //get background model url&filter
  const getBackgroundModelUrl = async () => {
    const result = await getBackgroundModelUrlApi(projectId);
    const data = result?.data;
    if (data && Array.isArray(data)) {
      const backgroundModels = data.map((d) => {
        return {
          ...d,
          fileURL: d.fileURL,
          visible: true,
        };
      });
      updateState("backgroundModels", backgroundModels);
    }
  };

  const getElementsData = async () => {
    try {
      const result = await getElementsDataApi(projectId);
      const data = result?.data;
      if (!data) showError("No element data returned from API.");
      const conTractors = new Set(data.filter((item) => item.contractor !== null).map((item) => item.contractor));
      setState((prevState) => {
        const newState = {
          ...prevState,
          elementsData: data,
        };
        if (Array.from(conTractors).length > 0) {
          newState.contractorElementValue = Array.from(conTractors);
        }
        colorRulesData(newState.elementsData);
        return newState;
      });
    } catch {
      showError("Received element data occurred error.");
    }
  };

  const colorRulesData = (data) => {
    const elementStatus = getElementStatus(data, state.currentDate);
    let allIds = data?.map((item) => item?.bimElementId);
    setElementIds((prevState) => ({
      ...prevState,
      ...elementStatus,
      all: allIds,
    }));
  };

  useEffect(() => {
    if (state.startDate && state.endDate) {
      if (dayjs(state.endDate).isBefore(state.startDate)) {
        showError("The End Date cannot be earlier than the Start Date.");
      }
    }
  }, [state.startDate, state.endDate]);

  useEffect(() => {
    if (!projectStatus) return;
    updateState(
      "statusOptions",
      projectStatus?.map((item) => ({
        value: item.value,
        label: item.label,
      }))
    );
  }, [projectStatus]);

  useEffect(() => {
    if (!allCompany || !allUsers) return;
    updateState("isLoadingFullScreen", true);
    getMainModel();
    getBackgroundModelUrl();
    getMembersTableData();
    getContractorsTableData();
    // getContractTypeData();
    const fetchData = async () => {
      await getAllUserData();
      await getCLPPersonData();
      await getProjectDetailsData();
      updateState("isLoadingFullScreen", false);
    };
    fetchData();
  }, [allCompany, allUsers]);

  useEffect(() => {
    setCommonProps(() => ({
      showFilterElementIds: elementIds.showFilter,
      allElementIds: elementIds.all,
      highlightElementIds: [],
      hideElementIds: elementIds.actualHide,
      delayedNotStartElementIds: elementIds.delayedNotStart,
      inProgressElementIds: elementIds.actualProgress,
      delayedElementIds: elementIds.delayed,
    }));
  }, [elementIds]);

  return (
    <Box sx={{ minWidth: 1100 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Project Management
          <img
            src="assets/icons/greyproject.svg"
            alt="Project"
            style={{
              width: "18px",
              height: "22px",
              marginLeft: "8px",
              verticalAlign: "text-top",
            }}
          />
        </Typography>
        {(user?.userRole === "System Administrator" || user?.accessLevel === 2) && (
          <Button
            variant="outlined"
            color="inherit"
            sx={{ ...buttonStyle, my: 0 }}
            onClick={() => {
              navigate("/modules/ProjectManagement");
            }}
          >
            Back
          </Button>
        )}
      </Box>
      <Box
        sx={{
          border: "1px solid #D0D5DE",
          p: 2,
          mt: 1,
          borderRadius: "10px",
          display: "flex",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, height: 400, width: "50%" }}>
          <IFCViewer
            ref={container}
            modelURL={state.modelURL}
            backgroundModels={state.backgroundModels}
            title={"3D Model View"}
            advanced
            {...commonProps}
          ></IFCViewer>
        </Box>
        <Box sx={{ flex: 1, minHeight: 400 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between",
              pb: 1.5,
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {state.originalState?.projectName}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              sx={{ borderRadius: "10px", py: 0.5 }}
              onClick={() => {
                navigate(`/modules/Project/${projectId}`);
              }}
            >
              Navigate to Project Page
            </Button>
          </Box>
          <Box
            sx={{
              height: "calc(100% - 43px)",
              background: "#D0D5DE",
              borderRadius: "10px",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Button
                  onClick={() => {
                    updateState("activeTab", "details");
                  }}
                  variant="contained"
                  color="inherit"
                  sx={{
                    background: state.activeTab === "details" ? "#717273!important" : "#ACADAF!important",
                    color: "#fff",
                    borderRadius: "10px",
                    py: "2px",
                    mr: 0.5,
                  }}
                >
                  Project Details
                </Button>
                <Button
                  onClick={() => {
                    updateState("activeTab", "settings");
                  }}
                  variant="contained"
                  color="inherit"
                  sx={{
                    background: state.activeTab === "settings" ? "#717273!important" : "#ACADAF!important",
                    color: "#fff",
                    borderRadius: "10px",
                    py: "2px",
                  }}
                >
                  Settings
                </Button>
              </Box>
              {/* {(user?.userRole === "System Administrator" || user?.accessLevel === 2) && ( */}
              {(user?.userRole === "System Administrator" || (user?.accessLevel === 2 && user?.userRole === "CLP User")) && (
                <Box>
                  {!state.editProjectDetails && (
                    <Button
                      onClick={() => {
                        setState((prevState) => ({
                          ...prevState,
                          editProjectDetails: !prevState.editProjectDetails,
                        }));
                      }}
                      variant="contained"
                      color="success"
                      sx={{ ...buttonStyle, mt: 1, mr: 1, mb: 0 }}
                    >
                      Update
                    </Button>
                  )}
                  {state.editProjectDetails && (
                    <>
                      <Button
                        onClick={updateProjectDetails}
                        variant="contained"
                        disabled={!isFormValid()}
                        color={isFormValid() ? "success" : "primary"}
                        sx={{ ...buttonStyle, mt: 1, mr: 1, mb: 0 }}
                      >
                        Save
                      </Button>
                      <Button
                        onClick={() => {
                          setState((prevState) => ({
                            ...prevState,
                            ...prevState.originalState,
                            editProjectDetails: !prevState.editProjectDetails,
                          }));
                        }}
                        variant="outlined"
                        color="inherit"
                        sx={{ ...buttonStyle, mt: 1, mr: 2, mb: 0 }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>
            <Box sx={{ px: 2 }}>
              {state.activeTab === "details" && (
                <Grid container columnSpacing={3} sx={{ mt: 1 }}>
                  {renderTextField("Project Name: ", "projectName", "Please Enter Project Name...", null, null, !state.editProjectDetails)}
                  <Grid item xs={6} display="flex" alignItems="center">
                    {!state.editProjectDetails ? (
                      <>
                        <Typography sx={typographyStyle}>Project Status:</Typography>
                        <Box
                          sx={{
                            display: "inline-block",
                            width: "15px",
                            height: "15px",
                            minWidth: "15px",
                            verticalAlign: "text-bottom",
                            margin: "0 6px 1px",
                            backgroundColor:
                              state.status === "On-Going"
                                ? "#00B176"
                                : state.status === "Finished"
                                ? "#A2ACA8"
                                : state.status === "Disable"
                                ? "#FF0303"
                                : "#fff",
                          }}
                        ></Box>
                        <Typography sx={{ ...typographyStyle, fontWeight: 500 }}>{state.status}</Typography>
                      </>
                    ) : (
                      <SelectComponent
                        options={state.statusOptions}
                        value={state.statusValue}
                        onChange={(value) => {
                          updateState("statusValue", value);
                        }}
                        selectLabel="Project Status:"
                        labelStyle={{
                          fontSize: "12px",
                          color: "#333",
                          minWidth: 120,
                          mr: 1,
                        }}
                        selectStyle={{ width: "100%", background: "#dbe1eb" }}
                        placeholder="Please Select Status..."
                      />
                    )}
                  </Grid>
                  {renderTextField("Project Number:", "projectNumber", "Please Enter Project Number...", null, null, !state.editProjectDetails)}
                  <Grid item xs={6} display="flex" alignItems="center">
                    {!state.editProjectDetails ? (
                      renderTextField("Project Main Contact:", "mainContactPersonName", "", null, null, !state.editProjectDetails)
                    ) : (
                      <SelectComponent
                        options={state.mainContactNameOptions}
                        value={state.mainContactPersonUserId}
                        onChange={contactNameChange}
                        selectLabel="Project Main Contact:"
                        labelStyle={{
                          fontSize: "12px",
                          color: "#333",
                          minWidth: 120,
                          mr: 1,
                        }}
                        selectStyle={{ width: "100%", background: "#dbe1eb" }}
                        placeholder="Please Select Contact..."
                      />
                    )}
                  </Grid>
                  {renderDatePicker("Start Date:", "startDate", !state.editProjectDetails)}
                  {renderTextField("Email Address:", "mainContactPersonEmail", "Please Select Contact...", null, null, true)}
                  {renderDatePicker("End Date:", "endDate", !state.editProjectDetails)}
                  <Grid item xs={6}></Grid>
                  {renderTextField("Project Location: ", "location", "Please Enter Project Location...", null, null, !state.editProjectDetails)}
                  <Grid item xs={6}></Grid>
                  {renderTextField("Project Summary:", "description", "Please Enter Project Summary...", true, 3, !state.editProjectDetails)}
                </Grid>
              )}
              {state.activeTab === "settings" && (
                <>
                  <Grid container columnSpacing={3} alignItems="center" sx={{ mt: 1 }}>
                    <Grid item xs={3}>
                      <Typography sx={typographyStyle}>Project Upload to Assai:</Typography>
                    </Grid>
                    <Grid item xs={3} container alignItems="center">
                      <Box position="relative">
                        {state.assaiEnabled ? (
                          <Typography
                            sx={{
                              position: "absolute",
                              top: "11px",
                              left: "28px",
                              color: "#fff",
                              zIndex: 2,
                            }}
                          >
                            Yes
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              position: "absolute",
                              top: "11px",
                              left: "36px",
                              color: "#fff",
                              zIndex: 2,
                            }}
                          >
                            No
                          </Typography>
                        )}
                        <Switch
                          disabled={!state.editProjectDetails}
                          checked={state.assaiEnabled}
                          onChange={() => {
                            updateState("assaiEnabled", !state.assaiEnabled);
                          }}
                          name="assaiEnabled"
                          color="primary"
                          sx={switchStyle}
                        />
                      </Box>
                      <Tooltip
                        title={Assai_note}
                        componentsProps={{
                          tooltip: {
                            sx: tooltipStyle,
                          },
                        }}
                      >
                        <HelpOutline sx={{ color: "#717273", cursor: "pointer" }} />
                      </Tooltip>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography sx={typographyStyle}>ACC:</Typography>
                    </Grid>
                    <Grid item xs={3} container alignItems="center">
                      <Box position="relative">
                        {state.accEnabled ? (
                          <Typography
                            sx={{
                              position: "absolute",
                              top: "11px",
                              left: "28px",
                              color: "#fff",
                              zIndex: 2,
                            }}
                          >
                            Yes
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              position: "absolute",
                              top: "11px",
                              left: "36px",
                              color: "#fff",
                              zIndex: 2,
                            }}
                          >
                            No
                          </Typography>
                        )}
                        <Switch
                          disabled={!state.editProjectDetails}
                          checked={state.accEnabled}
                          onChange={() => {
                            updateState("accEnabled", !state.accEnabled);
                          }}
                          name="accEnabled"
                          color="primary"
                          sx={switchStyle}
                        />
                      </Box>
                      <Tooltip
                        title={ACC_note}
                        componentsProps={{
                          tooltip: {
                            sx: tooltipStyle,
                          },
                        }}
                      >
                        <HelpOutline sx={{ color: "#717273", cursor: "pointer" }} />
                      </Tooltip>
                    </Grid>
                  </Grid>
                  <Grid container columnSpacing={3}>
                    {renderTextField("Client ID:", "assaiClientId", "Please Enter Client ID...", null, null, !state.editProjectDetails)}
                    {renderTextField("Client ID:", "accClientId", "Please Enter Client ID...", null, null, !state.editProjectDetails)}
                    {renderTextField("Secret Token:", "assaiSecretToken", "Please Enter Secret Token...", null, null, !state.editProjectDetails)}
                    {renderTextField("Client Secret:", "accClientSecret", "Please Enter Client Secret...", null, null, !state.editProjectDetails)}
                    <Grid item xs={6}></Grid>
                    {renderTextField("Revit Urn:", "accRevitUrn", "Please Enter Revit Urn...", null, null, !state.editProjectDetails)}
                  </Grid>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          height: "calc(50vh - 73px)",
          mt: 2,
          borderRadius: "10px",
          display: "flex",
          gap: 2,
        }}
      >
        <Box
          sx={{
            border: "1px solid #D0D5DE",
            p: 2,
            borderRadius: "10px",
            flex: 1,
            width: "50%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Assigned Members</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                placeholder="Search by name or etc"
                variant="outlined"
                size="small"
                onChange={(e) => {
                  updateState("searchMemberValue", e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchMembersButton();
                  }
                }}
                value={state.searchMemberValue}
                sx={{
                  my: 0.5,
                  width: 155,
                  ".MuiInputBase-inputSizeSmall": {
                    py: "4px!important",
                  },
                }}
              />
              <Button onClick={searchMembersButton} variant="contained" color="secondary" sx={{ borderRadius: "10px", py: "2px" }}>
                Search
              </Button>
              {(user?.userRole === "System Administrator" || user?.accessLevel === 2) && (
                <Button onClick={() => setAddNewMemberPopup(true)} variant="contained" color="secondary" sx={{ borderRadius: "10px", py: "2px" }}>
                  Add New Member
                </Button>
              )}
            </Box>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />
          <MainTable
            paperSx={{
              height: "calc(50vh - 170px)",
            }}
            sx={tableStyle}
            columns={membersTableHead}
            iconColumns={membersIconColumns}
            rows={state.membersTableData}
            headerStyles={{
              default: {
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #008AD8 100%)`,
                color: "#fff",
              },
            }}
            sortable
            sortableColumns={["memberName", "lastLogin"]}
            showPagination
            page={state.memberPage}
            rowsPerPage={state.memberRowsPerPage}
            onPageChange={(newPage) => setState((prevState) => ({ ...prevState, memberPage: newPage }))}
            onRowsPerPageChange={(newRowsPerPage) =>
              setState((prevState) => ({
                ...prevState,
                memberRowsPerPage: newRowsPerPage,
              }))
            }
            onSwitchChange={(rowId, columnName, switchValue) => {
              const targetRow = state.membersTableData.find((row) => row.id === rowId);
              const NameMaps = {
                costAccess: "Cost Module",
                reportModule: "Report Module",
                editRight: "Edit Right",
              };
              const Name = NameMaps[columnName] || columnName;
              setUpdatePermissionPopup(true);
              setUpdatePermissionMessage(
                `Are you sure you want to update the ${Name} Access of ${targetRow?.firstName + " " + targetRow?.lastName}?`
              );
              updateState("pendingSwitchUpdate", {
                rowId,
                columnName,
                switchValue,
                originalRow: targetRow,
              });
            }}
            isLoading={state.isLoadingMember}
          />
        </Box>
        <Box
          sx={{
            border: "1px solid #D0D5DE",
            p: 2,
            borderRadius: "10px",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Contractors</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                placeholder="Search by name or etc"
                variant="outlined"
                size="small"
                onChange={(e) => {
                  updateState("searchContractorValue", e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchContractorsButton();
                  }
                }}
                value={state.searchContractorValue}
                sx={{
                  my: 0.5,
                  width: 155,
                  ".MuiInputBase-inputSizeSmall": {
                    py: "4px!important",
                  },
                }}
              />
              <Button onClick={searchContractorsButton} variant="contained" color="secondary" sx={{ borderRadius: "10px", py: "2px" }}>
                Search
              </Button>
              {/* {(user?.userRole === "System Administrator" || user?.accessLevel === 2) && ( */}
              {(user?.userRole === "System Administrator" || (user?.accessLevel === 2 && user?.userRole === "CLP User")) && (
                <Button onClick={() => setAddNewContractorPopup(true)} variant="contained" color="secondary" sx={{ borderRadius: "10px", py: "2px" }}>
                  Add New Contractor
                </Button>
              )}
            </Box>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />
          <MainTable
            paperSx={{
              height: "calc(50vh - 170px)",
            }}
            sx={tableStyle}
            columns={contractorsTableHead}
            iconColumns={contractorsIconColumns}
            rows={state.contractorsTableData}
            headerStyles={{
              default: {
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #008AD8 100%)`,
                color: "#fff",
              },
            }}
            showPagination
            page={state.contractorPage}
            rowsPerPage={state.contractorRowsPerPage}
            onPageChange={(newPage) =>
              setState((prevState) => ({
                ...prevState,
                contractorPage: newPage,
              }))
            }
            onRowsPerPageChange={(newRowsPerPage) =>
              setState((prevState) => ({
                ...prevState,
                contractorRowsPerPage: newRowsPerPage,
              }))
            }
            onSwitchChange={(rowId, columnName, switchValue) => {
              const targetRow = state.contractorsTableData.find((row) => row.id === rowId);
              setUpdateContractorCostPopup(true);
              setUpdateContractorCostMessage(`Are you sure you want to update the Cost Module Access of ${targetRow?.company}?`);
              updateState("pendingSwitchUpdate", {
                rowId,
                columnName,
                switchValue,
                originalRow: targetRow,
              });
            }}
            isLoading={state.isLoadingContractor}
          />
        </Box>
      </Box>
      <Popup
        open={updateMemberPopup}
        onClose={() => setUpdateMemberPopup(false)}
        modalStyle={{
          "& .MuiBox-root.css-ft9d2q": {
            borderRadius: "10px 10px 0 0",
          },
        }}
        title="Update Project Access"
      >
        <Box
          sx={{
            width: 500,
            display: "flex",
            mx: 4,
            mt: 2,
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", minWidth: 120, fontWeight: 600 }}>Member Name:</Typography>
            <TextField sx={textFieldStyle} disabled={true} variant="outlined" size="small" value={projectAccessInfor.memberName} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", minWidth: 120, fontWeight: 600 }}>User Role:</Typography>
            <TextField sx={textFieldStyle} disabled={true} variant="outlined" size="small" value={projectAccessInfor.userRole} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SelectComponent
              options={[
                { value: "Project Member", label: "Project Member" },
                {
                  value: "Project Administrator",
                  label: "Project Administrator",
                },
              ]}
              value={projectAccessInfor.accessLevel}
              onChange={(value) => {
                setProjectAccessInfor((preState) => ({
                  ...preState,
                  accessLevel: value,
                }));
              }}
              selectLabel="Access Level:"
              labelStyle={{
                fontSize: "11px",
                fontWeight: 600,
                minWidth: 120,
                mr: 1,
              }}
              selectStyle={{ width: "100%" }}
              placeholder="Please Select Access Level..."
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", minWidth: 120, fontWeight: 600 }}>Edit Right:</Typography>
            <Switch
              checked={projectAccessInfor.editRight}
              onChange={() => {
                setProjectAccessInfor((preState) => ({
                  ...preState,
                  editRight: !preState.editRight,
                }));
              }}
              name="editRight"
              color="primary"
              sx={switchStyle}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", minWidth: 120, fontWeight: 600 }}>Report Module:</Typography>
            <Switch
              checked={projectAccessInfor.reportModule}
              onChange={() => {
                setProjectAccessInfor((preState) => ({
                  ...preState,
                  reportModule: !preState.reportModule,
                }));
              }}
              name="reportModule"
              color="primary"
              sx={switchStyle}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", minWidth: 120, fontWeight: 600 }}>Cost Module:</Typography>
            <Switch
              checked={projectAccessInfor.costAccess}
              onChange={() => {
                setProjectAccessInfor((preState) => ({
                  ...preState,
                  costAccess: !preState.costAccess,
                }));
              }}
              name="costAccess"
              color="primary"
              sx={switchStyle}
            />
          </Box>
          <Box textAlign="center">
            <Button onClick={updateProjectAccessButton} sx={buttonStyle} variant="contained" color="success">
              Update
            </Button>
          </Box>
        </Box>
      </Popup>
      <Popup
        open={updateContractorPopup}
        onClose={() => setUpdateContractorPopup(false)}
        modalStyle={{
          "& .MuiBox-root.css-ft9d2q": {
            borderRadius: "10px 10px 0 0",
          },
        }}
        title="Update Contractor"
      >
        <Box
          sx={{
            width: 500,
            display: "flex",
            mx: 4,
            mt: 2,
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", minWidth: 140, fontWeight: 600 }}>Company:</Typography>
            <TextField sx={textFieldStyle} disabled={true} variant="outlined" size="small" value={contractorInfor.company} />
          </Box>
          {/* <Box sx={{ display: "flex", alignItems: "center" }}>
            <SelectComponent
              options={state.categoryOptions}
              value={contractorInfor.category}
              onChange={(value) => {
                setContractorInfor((prevState) => ({
                  ...prevState,
                  category: value,
                }));
              }}
              selectLabel="Category:"
              labelStyle={{
                fontSize: "11px",
                fontWeight: 600,
                minWidth: 140,
                mr: 1,
              }}
              selectStyle={{ width: "100%" }}
              placeholder="Please Select Category..."
            />
          </Box> */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SelectComponent
              options={state.userByCompanyOptions}
              value={contractorInfor.pmName}
              onChange={(value) => {
                const selectedEmail = state.userEmailMap[value] || "";
                setContractorInfor((prevState) => ({
                  ...prevState,
                  pmName: value,
                  pmEmail: selectedEmail,
                }));
              }}
              selectLabel="Project Manager Name:"
              labelStyle={{
                fontSize: "11px",
                fontWeight: 600,
                minWidth: 140,
                mr: 1,
              }}
              selectStyle={{ width: "100%" }}
              placeholder="Please Select Manager Name..."
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", minWidth: 140, fontWeight: 600 }}>Project Manager Email:</Typography>
            <TextField sx={textFieldStyle} disabled={true} variant="outlined" size="small" value={contractorInfor.pmEmail} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", minWidth: 140, fontWeight: 600 }}>Cost Module:</Typography>
            <Switch
              checked={contractorInfor.costModule}
              onChange={() => {
                setContractorInfor((preState) => ({
                  ...preState,
                  costModule: !preState.costModule,
                }));
              }}
              name="costModule"
              color="primary"
              sx={switchStyle}
            />
          </Box>
          <Box textAlign="center">
            <Button onClick={updateContractorButton} sx={buttonStyle} variant="contained" color="success">
              Update
            </Button>
          </Box>
        </Box>
      </Popup>
      <AddNewMemberPopup
        open={addNewMemberPopup}
        onClose={() => setAddNewMemberPopup(false)}
        options={state.allUserOptions?.filter((item) => {
          // const isNotDisabled = item.status !== "Disable";
          const isNotAlreadyAdded = !state.membersTableData.map((d) => d.id).includes(item.userId);
          const isSameCompany = user?.userRole === "Contractor" ? item.company === user.company : true;
          return isNotAlreadyAdded && isSameCompany;
        })}
        // options={state.allUserOptions?.filter((item) => item.status !== "Disable" && !state.membersTableData.map((d) => d.id).includes(item.userId))}
        onCreate={handleAddMember}
        onSetError={(value) => {
          showError(value);
        }}
      />
      <AddNewContractorPopup
        open={addNewContractorPopup}
        onClose={() => setAddNewContractorPopup(false)}
        options={allCompany?.filter((item) => item.label !== "CLP" && !state.contractorsTableData.map((d) => d.company).includes(item.label))}
        // category={state.categoryOptions}
        onCreate={handleAddContractor}
        onSetError={(value) => {
          showError(value);
        }}
      />
      <ConfirmPopup
        open={removeMemberPopup}
        message={removeMemberMessage}
        type="error"
        buttonLabelText="No"
        proceedButtonLabelText="Yes"
        iconSrc="assets/icons/error.svg"
        onCancel={() => {
          setRemoveMemberPopup(false);
        }}
        onConfirm={removeMembersButton}
      ></ConfirmPopup>
      <ConfirmPopup
        open={removeContractorPopup}
        message={removeMemberMessage}
        type="error"
        buttonLabelText="No"
        proceedButtonLabelText="Yes"
        iconSrc="assets/icons/error.svg"
        onCancel={() => {
          setRemoveContractorPopup(false);
        }}
        onConfirm={removeContractorButton}
      ></ConfirmPopup>
      <ConfirmPopup
        open={updatePermissionPopup}
        message={updatePermissionMessage}
        type="error"
        buttonLabelText="No"
        proceedButtonLabelText="Yes"
        iconSrc="assets/icons/error.svg"
        onCancel={() => {
          setUpdatePermissionPopup(false);
        }}
        onConfirm={confirmUpdatePermission}
      ></ConfirmPopup>
      <ConfirmPopup
        open={updateContractorCostPopup}
        message={updateContractorCostMessage}
        type="error"
        buttonLabelText="No"
        proceedButtonLabelText="Yes"
        iconSrc="assets/icons/error.svg"
        onCancel={() => {
          setUpdateContractorCostPopup(false);
        }}
        onConfirm={confirmUpdateContractorCost}
      ></ConfirmPopup>
      <FullScreenLoading isLoading={state.isLoadingFullScreen} />
    </Box>
  );
};

export default ProjectDetails;
